/**
 * Base API client — injects Netlify Identity JWT into every request.
 * Speaker portal requests pass a token header instead.
 */

let _identityUser = null;

/** Register the Netlify Identity user so the client can get a fresh token */
export function setIdentityUser(user) {
  _identityUser = user;
}

/** Force logout — called automatically on 401 to clear stale sessions */
function forceLogout() {
  _identityUser = null;
  window.netlifyIdentity?.logout();
}

async function getAuthHeader() {
  if (!_identityUser) return {};
  const token = await _identityUser.jwt();
  return { Authorization: `Bearer ${token}` };
}

/**
 * Core fetch wrapper.
 * @param {string} path  e.g. '/api/speakers'
 * @param {RequestInit} [opts]
 * @returns {Promise<any>}
 */
async function apiFetch(path, opts = {}) {
  const authHeader = await getAuthHeader();
  const res = await fetch(path, {
    ...opts,
    headers: {
      ...authHeader,
      ...(opts.body && !(opts.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(opts.headers ?? {}),
    },
  });

  if (res.status === 204) return null;

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = text;
  }

  if (!res.ok) {
    // Stale or expired session — clear it so the user is sent back to login
    if (res.status === 401) forceLogout();
    const msg = data?.error ?? data ?? `HTTP ${res.status}`;
    throw Object.assign(new Error(msg), { status: res.status, data });
  }

  return data;
}

export const api = {
  get:    (path, opts = {}) => apiFetch(path, { ...opts, method: 'GET' }),
  post:   (path, body, opts = {}) =>
    apiFetch(path, { ...opts, method: 'POST', body: JSON.stringify(body) }),
  put:    (path, body, opts = {}) =>
    apiFetch(path, { ...opts, method: 'PUT', body: JSON.stringify(body) }),
  patch:  (path, body, opts = {}) =>
    apiFetch(path, { ...opts, method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path, opts = {}) => apiFetch(path, { ...opts, method: 'DELETE' }),

  /** Upload a file as raw bytes */
  uploadFile: (path, file, opts = {}) =>
    apiFetch(path, {
      ...opts,
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type,
        'X-Filename': file.name,
        ...(opts.headers ?? {}),
      },
    }),

  /** Speaker portal requests — no Identity JWT, use token header */
  portal: (path, speakerToken, opts = {}) =>
    apiFetch(path, {
      ...opts,
      headers: {
        'X-Speaker-Token': speakerToken,
        ...(opts.headers ?? {}),
      },
    }),
};
